import { useCallback, useEffect, useState } from 'react';
import ApiService from '../services/ApiService';
import SocketService from '../services/SocketService';

// Matches the header notification bell's polling interval (Header.jsx).
const POLL_MS = 30000;

/**
 * Sidebar badge counts:
 *  - membershipCount: new subscriber requests awaiting a decision, plus
 *    cancellation requests awaiting one (sum, shown on the parent "Memberships"
 *    entry).
 *  - subscriberCount / cancellationCount: the same two numbers split out, for
 *    the "Subscribers" / "Cancellation Requests" sub-sidebar entries.
 *  - bookingCount: newly placed (Pending) bookings.
 *
 * Polled on an interval (no dedicated "counts changed" event exists), and the
 * booking count is also bumped immediately on the 'newBooking' socket event
 * so it doesn't wait for the next poll — NewBookingPopup listens for the same
 * event independently, for its own toast/modal.
 */
export function useAdminCounts() {
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [cancellationCount, setCancellationCount] = useState(0);
  const [bookingCount, setBookingCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const [membershipRes, bookingRes] = await Promise.all([
        ApiService.request({ method: 'GET', url: 'admin/membership-plans/pending-counts' }),
        ApiService.request({ method: 'GET', url: 'admin/bookings/stats' }),
      ]);
      const m = membershipRes.data?.data;
      if (m) {
        setSubscriberCount(m.pending_subscribers || 0);
        setCancellationCount(m.pending_cancellations || 0);
      }
      const b = bookingRes.data?.data;
      if (b) setBookingCount(b.pending_bookings || 0);
    } catch (error) {
      console.error('Error loading sidebar counts:', error);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    return SocketService.subscribe('newBooking', () => {
      setBookingCount((count) => count + 1);
    });
  }, []);

  return {
    membershipCount: subscriberCount + cancellationCount,
    subscriberCount,
    cancellationCount,
    bookingCount,
  };
}
